import Head from 'next/head';
import { ReactNode } from 'react';
import { useRouter } from 'next/router';
import Footer from '@/components/Footer';
import { SITE_URL } from '@/lib/seoRoutes';
import SiteNavbar from './SiteNavbar';

type SiteLayoutProps = {
  title: string;
  description: string;
  children: ReactNode;
  keywords?: string[];
  image?: string;
  noIndex?: boolean;
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
};

const DEFAULT_IMAGE = `${SITE_URL}/images/logo.svg`;

const SiteLayout = ({
  title,
  description,
  children,
  keywords = [],
  image,
  noIndex = false,
  structuredData: customStructuredData,
}: SiteLayoutProps) => {
  const router = useRouter();
  const cleanPath = router.asPath.split(/[?#]/)[0] || '/';
  const canonicalUrl = `${SITE_URL}${cleanPath === '/' ? '' : cleanPath}`;
  const imageUrl = image || DEFAULT_IMAGE;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    headline: title,
    description,
    url: canonicalUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: 'DocMoonlight',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'DocMoonlight',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: DEFAULT_IMAGE,
      },
    },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: imageUrl,
    },
    ...(keywords.length > 0 ? { keywords: keywords.join(', ') } : {}),
  };
  const structuredDataItems = [
    structuredData,
    ...(Array.isArray(customStructuredData)
      ? customStructuredData
      : customStructuredData
        ? [customStructuredData]
        : []),
  ];

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta key='description' name='description' content={description} />
        {keywords.length > 0 && (
          <meta key='keywords' name='keywords' content={keywords.join(', ')} />
        )}
        <meta
          key='robots'
          name='robots'
          content={noIndex ? 'noindex, nofollow' : 'index, follow'}
        />
        <link key='canonical' rel='canonical' href={canonicalUrl} />
        <meta key='og:type' property='og:type' content='website' />
        <meta key='og:site_name' property='og:site_name' content='DocMoonlight' />
        <meta key='og:title' property='og:title' content={title} />
        <meta
          key='og:description'
          property='og:description'
          content={description}
        />
        <meta key='og:url' property='og:url' content={canonicalUrl} />
        <meta key='og:image' property='og:image' content={imageUrl} />
        <meta
          key='twitter:card'
          name='twitter:card'
          content='summary_large_image'
        />
        <meta key='twitter:title' name='twitter:title' content={title} />
        <meta
          key='twitter:description'
          name='twitter:description'
          content={description}
        />
        <meta key='twitter:image' name='twitter:image' content={imageUrl} />
        {structuredDataItems.map((item, index) => (
          <script
            key={`structured-data-${index}`}
            type='application/ld+json'
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(item),
            }}
          />
        ))}
      </Head>
      <div className='min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(var(--theme-accent-soft-rgb),0.24),_transparent_24%),linear-gradient(180deg,var(--theme-page-start)_0%,var(--theme-page-mid)_40%,var(--theme-page-end)_100%)] text-[var(--theme-dark)]'>
        <SiteNavbar />
        <main>{children}</main>
        <Footer />
      </div>
    </>
  );
};

export default SiteLayout;
